import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  CreditCard, Palette, UserPlus, BookOpen, Check, ChevronRight, ChevronLeft,
  Sparkles, Crown, Building2, ArrowRight
} from "lucide-react";
import api from "../../lib/api";
import Logo from "../../components/shared/Logo";


const STEPS = [
  { id: 1, label: "Select Plan", icon: CreditCard },
  { id: 2, label: "Branding", icon: Palette },
  { id: 3, label: "Invite Team", icon: UserPlus },
  { id: 4, label: "First Course", icon: BookOpen },
  { id: 5, label: "Complete", icon: Sparkles },
];

const TenantOnboardingWizard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [branding, setBranding] = useState({ primary_color: "#1B4F8A", company_name: "", tagline: "" });
  const [emails, setEmails] = useState(["", "", ""]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, coursesRes] = await Promise.all([
          api.get("/onboarding/available-plans"),
          api.get("/onboarding/available-courses"),
        ]);
        setPlans(plansRes.data.plans || []);
        setCourses(coursesRes.data.courses || []);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const processStep = async (stepName, data) => {
    setProcessing(true);
    try {
      await api.post("/onboarding/wizard-step", { step: stepName, data });
      return true;
    } catch (e) {
      toast.error("Failed to save step");
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      if (!selectedPlan) return toast.error("Select a plan to continue");
      const ok = await processStep("select_plan", { plan_id: selectedPlan });
      if (ok) setStep(2);
    } else if (step === 2) {
      const ok = await processStep("configure_branding", branding);
      if (ok) setStep(3);
    } else if (step === 3) {
      const validEmails = emails.filter(e => e && e.includes("@"));
      if (validEmails.length > 0) {
        await processStep("invite_users", { emails: validEmails });
      }
      setStep(4);
    } else if (step === 4) {
      if (selectedCourse) {
        await processStep("assign_course", { course_id: selectedCourse });
      }
      setStep(5);
    } else if (step === 5) {
      await processStep("complete", {});
      toast.success("Onboarding complete! Redirecting to dashboard...");
      setTimeout(() => navigate("/tenant/dashboard"), 1500);
    }
  };

  const planIcons = { "plan-starter": CreditCard, "plan-pro": Crown, "plan-enterprise": Building2 };
  const planColors = { "plan-starter": "#0E8A55", "plan-pro": "#1B4F8A", "plan-enterprise": "#7C3AED" };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1520] via-[#0F1D2E] to-[#0B1520] flex items-center justify-center p-4" data-testid="tenant-onboarding-wizard">
      <div className="w-full max-w-3xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="lg" showText className="mx-auto text-white" />
          <p className="text-white/60 mt-2 text-sm">Set up your learning environment in minutes</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <div key={s.id} className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-orange-500 text-white' : 'bg-white/10 text-white/40'}`}>
                  {isDone ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                </div>
                {i < STEPS.length - 1 && <div className={`w-12 h-0.5 ${isDone ? 'bg-green-500' : 'bg-white/10'}`} />}
              </div>
            );
          })}
        </div>
        <p className="text-center text-white/80 text-sm font-medium mb-6">{STEPS[step - 1]?.label}</p>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">

          {/* Step 1: Select Plan */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>Choose Your Plan</h2>
              <p className="text-white/60 text-sm">Select the plan that fits your organization's needs</p>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {plans.map(plan => {
                  const PlanIcon = planIcons[plan.id] || CreditCard;
                  const color = planColors[plan.id] || "#1B4F8A";
                  return (
                    <button key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                      className={`p-4 rounded-xl border text-left transition-all ${selectedPlan === plan.id ? 'border-orange-400 bg-orange-500/10' : 'border-white/10 hover:border-white/20 bg-white/5'}`}
                      data-testid={`plan-${plan.id}`}>
                      <PlanIcon className="w-6 h-6 mb-2" style={{ color }} />
                      <h3 className="font-bold text-white">{plan.name}</h3>
                      <p className="text-2xl font-extrabold text-white mt-1">${plan.price_monthly}<span className="text-sm font-normal text-white/50">/mo</span></p>
                      <p className="text-xs text-white/50 mt-1">{plan.max_users ? `Up to ${plan.max_users} users` : "Unlimited users"}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Branding */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>Configure Branding</h2>
              <p className="text-white/60 text-sm">Customize the look and feel of your learning portal</p>
              <div className="space-y-3 mt-4">
                <div>
                  <label className="text-sm font-medium text-white/80">Company Name</label>
                  <Input value={branding.company_name} onChange={e => setBranding({...branding, company_name: e.target.value})} placeholder="Your Company Name" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" data-testid="branding-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-white/80">Tagline</label>
                  <Input value={branding.tagline} onChange={e => setBranding({...branding, tagline: e.target.value})} placeholder="e.g., Training that transforms" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <label className="text-sm font-medium text-white/80">Brand Color</label>
                  <div className="flex gap-3 mt-2">
                    {["#1B4F8A", "#C7322A", "#0E8A55", "#7C3AED", "#D97706", "#0D9488"].map(c => (
                      <button key={c} onClick={() => setBranding({...branding, primary_color: c})}
                        className={`w-10 h-10 rounded-lg transition-all ${branding.primary_color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110' : 'hover:scale-105'}`}
                        style={{ background: c }} data-testid={`color-${c.replace('#','')}`} />
                    ))}
                  </div>
                </div>
                {/* Preview */}
                <div className="mt-4 p-4 rounded-xl" style={{ background: branding.primary_color + "20", border: `1px solid ${branding.primary_color}40` }}>
                  <p className="text-sm font-bold text-white">{branding.company_name || "Your Company"}</p>
                  <p className="text-xs text-white/60 mt-0.5">{branding.tagline || "Your tagline here"}</p>
                  <div className="mt-2 h-2 rounded-full" style={{ background: branding.primary_color, width: "60%" }} />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Invite Team */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>Invite Your Team</h2>
              <p className="text-white/60 text-sm">Add team members to get started. You can always invite more later.</p>
              <div className="space-y-3 mt-4">
                {emails.map((email, i) => (
                  <Input key={i} value={email} onChange={e => { const newEmails = [...emails]; newEmails[i] = e.target.value; setEmails(newEmails); }}
                    placeholder={`team-member${i + 1}@company.com`}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                    data-testid={`invite-email-${i}`} />
                ))}
                <Button variant="ghost" className="text-white/60 hover:text-white" onClick={() => setEmails([...emails, ""])}>+ Add another</Button>
              </div>
              <p className="text-xs text-white/40">Tip: You can skip this step and invite users later from Settings</p>
            </div>
          )}

          {/* Step 4: Assign Course */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>Assign First Course</h2>
              <p className="text-white/60 text-sm">Select a course to assign to all your learners</p>
              <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto pr-2">
                {courses.length === 0 ? (
                  <p className="text-white/40 text-center py-8">No published courses available yet</p>
                ) : courses.map(c => (
                  <button key={c.id} onClick={() => setSelectedCourse(c.id)}
                    className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${selectedCourse === c.id ? 'border-orange-400 bg-orange-500/10' : 'border-white/10 hover:border-white/20 bg-white/5'}`}
                    data-testid={`course-${c.id}`}>
                    <BookOpen className="w-5 h-5 text-white/50" />
                    <div>
                      <p className="text-sm font-medium text-white">{c.title}</p>
                      <p className="text-xs text-white/40">{c.category || "General"} · {c.format || "online"}</p>
                    </div>
                    {selectedCourse === c.id && <Check className="w-5 h-5 text-orange-400 ml-auto" />}
                  </button>
                ))}
              </div>
              <p className="text-xs text-white/40">Tip: You can skip this and assign courses later from the Course Catalog</p>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>You're All Set!</h2>
              <p className="text-white/60 mt-2">Your learning environment is ready. Click below to go to your dashboard.</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-white/10">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-white/60 hover:text-white">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : <div />}
            <div className="flex gap-3">
              {step < 5 && step > 1 && (
                <Button variant="ghost" onClick={() => setStep(step + 1)} className="text-white/40 hover:text-white">Skip</Button>
              )}
              <Button onClick={nextStep} disabled={processing}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0 px-6"
                data-testid="wizard-next-btn">
                {processing ? "Saving..." : step === 5 ? "Go to Dashboard" : "Continue"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantOnboardingWizard;
